import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const storedToken = localStorage.getItem('token');
  const storedUser = localStorage.getItem('user');

  const [token, setToken] = useState(storedToken || null);
  const [user, setUser] = useState(storedUser ? JSON.parse(storedUser) : null);
  const [isLoggedIn, setIsLoggedIn] = useState(!!storedToken);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // ✅ تسجيل مستخدم جديد
  const registerUser = async (formData) => {
    setLoading(true);
    clearMessages();

    try {
      const response = await axios.post(
        'https://www.quickpickdeal.com/api/auth/registration',
        {
          FullName: formData.fullName,
          Email: formData.email,
          PhoneNumber: formData.phoneNumber,
          Password: formData.password,
          ConfirmPassword: formData.confirmPassword,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

      if (response.data && response.data.Success) {
        setSuccess('Registration successful! Please log in.');
      } else {
        setError(response.data?.Error || 'Registration failed!');
      }
    } catch (err) {
      let message = 'Registration failed. Please try again.';
      if (err.response?.status === 409) message = 'Email already exists!';
      else if (err.response?.status === 400) {
        const confirmErrors = err.response?.data?.errors?.ConfirmPassword;
        if (confirmErrors?.length > 0) message = confirmErrors.join(', ');
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ تسجيل الدخول
  const loginUser = async (credentials) => {
    setLoading(true);
    clearMessages();

    try {
      const response = await axios.post(
        'https://www.quickpickdeal.com/api/auth/login',
        {
          Email: credentials.email,
          Password: credentials.password,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

      if (response.data && response.data.Success) {
        const userData = response.data.Data;
        const userToken =
          response.data.Data?.AccessToken ||
          response.data.Token ||
          response.data.Data?.token;

        if (userToken) {
          localStorage.setItem('token', userToken);
          localStorage.setItem('user', JSON.stringify(userData));
          setToken(userToken);
          setUser(userData);
          setIsLoggedIn(true);
          setSuccess('Login successful!');
        } else {
          setError('Login failed: No token received.');
        }
      } else {
        setError(response.data?.Error || 'Login failed!');
      }
    } catch (err) {
      let message = 'Login failed. Please try again.';
      if (err.response?.status === 401) message = 'Invalid email or password!';
      else if (err.response?.status === 404) message = 'Account not found!';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ تسجيل الخروج (يرجعه للـ Home)
  const logoutUser = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setIsLoggedIn(false);
    setSuccess('Logged out successfully!');
    setError(null);
    navigate('/'); // ✅ تحويل المستخدم للـ Home بعد الـ Logout
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        error,
        success,
        isLoggedIn,
        loginUser,
        logoutUser,
        registerUser,
        clearMessages,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
